/**
 * @file billLocalEdits.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-05
 * @description The parts of a bill this system keeps for itself — the
 *              supplier's PDF, notes and PO links — which can be changed on
 *              ANY bill, whoever made it and whatever its status (approved
 *              and paid included). None of them sends a line or a figure to
 *              Xero. Words for the bill window (BillModal); the server has
 *              the rules (routes/bills.js, billService.setPoLinks /
 *              updateNotes).
 */

import { billInXero } from './billStatus';

/**
 * What happened to the PDF, in a sentence, from POST /api/bills/:id/attachment.
 *
 * @param {{ replaced?: boolean, xero_attachment?: object|null }} data
 * @returns {{ text: string, type: 'success'|'info'|'warning' }}
 */
export function attachmentOutcome(data) {
  const verb = data?.replaced ? 'replaced' : 'saved';
  const x = data?.xero_attachment || null;
  if (!x) return { text: `PDF ${verb}.`, type: 'success' };
  if (x.ok) return { text: `PDF ${verb}, and attached to the bill in Xero.`, type: 'success' };
  switch (x.skipped) {
    case 'xero_made':
      return {
        text: `PDF ${verb} here. This bill was keyed in Xero, so the PDF is not sent there — Xero keeps its own attachments.`,
        type: 'info',
      };
    case 'not_sent':
      return { text: `PDF ${verb}. It goes to Xero with the bill when the bill is sent.`, type: 'success' };
    case 'switched_off':
      return { text: `PDF ${verb} here. Sending bills to Xero is switched off, so it was not attached in Xero.`, type: 'info' };
    case 'in_progress':
      return {
        text: `PDF ${verb} here. The bill was being sent to Xero at that moment, so the PDF did not go with it — choose Replace PDF again in a minute to send it.`,
        type: 'warning',
      };
    case 'migration':
      return { text: `PDF ${verb} here. It could not be sent to Xero yet (a database update is outstanding).`, type: 'warning' };
    default:
      break;
  }
  const why = x.error ? ` (${x.error})` : '';
  if (x.pdf_held_note && x.pdf_held_note.ok) {
    return {
      text: `PDF ${verb} here. Xero would not take the file${why}, so a note on the bill's History in Xero says the PDF is held in Excel Agri System.`,
      type: 'info',
    };
  }
  return { text: `PDF ${verb} here, but it could not be attached in Xero${why}.`, type: 'warning' };
}

/**
 * Asked before replacing a PDF: on a bill entered here that is in Xero the
 * earlier PDF stays attached there (nothing here can take it off).
 */
export function replacePdfQuestion(bill) {
  const inXero = bill?.source === 'local' && billInXero(bill);
  return `Replace ${bill?.attachment_filename || 'the PDF'} with a new one? The old file is kept in the bill's history.`
    + (inXero ? ' In Xero the earlier PDF (if Xero took it) stays attached alongside the new one.' : '');
}

/** Can a PO be linked to this bill (taking one off is always allowed)? */
export function canLinkPos(bill) {
  const st = String(bill?.status || '').toLowerCase();
  if (st === 'voided' || st === 'deleted') return { ok: false, reason: `This bill is ${st} — POs can be taken off it, not added` };
  if (!bill?.account_id) return { ok: false, reason: 'The supplier is not linked to a contact here, so its POs cannot be listed' };
  return { ok: true, reason: null };
}
